import React, { useEffect, useMemo, useState } from "react";
import { Box, Tab } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import CommonPage from "../../components/common-page";
import useLanguage from "../../hooks/useLanguage";
import useMemorize from "../../hooks/useMemorize";
import useToaster from "../../hooks/useToaster";
import useTrendScout from "../../hooks/useTrendScout";
import useUserData from "../../hooks/useUserData";

import ArticlesTab from "./tabs/articles.jsx";
import SettingsTab from "./tabs/settings.jsx";
import {
  PulseDot,
  StatusPill,
  StatusText,
  StyledTabs,
} from "./style.jsx";
import { formatRelative } from "./utils.js";

const VALID_TABS = new Set(["articles", "settings"]);

export default function TrendScout() {
  const [{ pages: { trendscout: text } }] = useLanguage();
  const { addToaster } = useToaster();
  const { getConfig, getStatus, triggerRefresh } = useTrendScout();
  // Settings (global coverage config) and the scan trigger are admin-only on
  // the API — hide both rather than let a click land on a 403.
  const isAdmin = useUserData()?.role === "admin";

  const [tabRaw, setTab] = useMemorize("trendscout");
  // A ?topic= deep-link from the dashboard is a request for the feed — it must
  // beat an admin's remembered Settings tab, or the link silently does nothing.
  const params = new URLSearchParams(window.location.search);
  const deepLinked = params.has("topic") || params.has("guid");
  const tab =
    isAdmin && !deepLinked && VALID_TABS.has(tabRaw) ? tabRaw : "articles";

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadMeta = async () => {
    setMetaLoading(true);
    try {
      const [c, s] = await Promise.all([
        getConfig().catch(() => null),
        getStatus().catch(() => null),
      ]);
      setConfig(c);
      setStatus(s);
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await triggerRefresh();
      if (res?.enqueued) {
        addToaster({
          message: text.toaster.refreshTriggered,
          severity: "success",
        });
        // Refresh status shortly after to catch nextRunAt update.
        setTimeout(loadMeta, 500);
      } else {
        addToaster({ message: text.toaster.refreshFailed, severity: "error" });
      }
    } catch (_) {
      addToaster({ message: text.toaster.refreshFailed, severity: "error" });
    } finally {
      setRefreshing(false);
    }
  };

  const lastScanLabel = useMemo(() => {
    if (refreshing) return text.status.scanning;
    if (!status?.lastRunAt) return text.status.never;
    const rel = formatRelative(status.lastRunAt, text.relative);
    return `${text.status.lastScan} ${rel}`;
  }, [status, refreshing, text]);

  const schedulerActive = !!status?.schedulerEnabled;

  const StatusEl = (
    <StatusPill
      key="status"
      onClick={isAdmin ? handleRefresh : undefined}
      $disabled={refreshing || !isAdmin}
      role={isAdmin ? "button" : undefined}
      aria-label={isAdmin ? text.status.scanNow : undefined}
      title={isAdmin ? text.status.scanNow : undefined}
    >
      <PulseDot $active={schedulerActive} />
      <StatusText>{lastScanLabel}</StatusText>
      {isAdmin && (
        <RefreshRoundedIcon
          fontSize="small"
          sx={{
            fontSize: 14,
            color: "text.secondary",
            transition: "transform 0.4s ease",
            transform: refreshing ? "rotate(180deg)" : "none",
          }}
        />
      )}
    </StatusPill>
  );

  return (
    <CommonPage
      title={text.title}
      subtitle={text.subtitle}
      actions={[StatusEl]}
    >
      <StyledTabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        aria-label="trendscout-tabs"
      >
        <Tab label={text.tabs.articles} value="articles" />
        {isAdmin && <Tab label={text.tabs.settings} value="settings" />}
      </StyledTabs>

      <Box
        sx={{
          // The card grid scrolls inside the page, so this column has to hand
          // it the leftover height instead of growing past the viewport.
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: tab === "articles" ? "hidden" : "auto",
        }}
      >
        {tab === "articles" && (
          <ArticlesTab status={status} text={text} />
        )}
        {tab === "settings" && (
          <SettingsTab
            config={config}
            status={status}
            loading={metaLoading}
            text={text}
            onSaved={loadMeta}
          />
        )}
      </Box>
    </CommonPage>
  );
}
